import { Card, Radio, RadioChangeEvent } from "antd";
import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { GetDashboardIssueCatData } from "../../../../utils/api.constant";
import { eResultCode } from "../../../../utils/enum";
import useFetch from "../../../../hooks/useFetch";
import { ApexOptions } from "apexcharts"; // Import ApexOptions
import DateUtils from "../../../../utils/date";
import { TDropdownOptions } from "../../../../types/config";
import {
  dateFormat,
  dateOnlyFormat,
  monthOnlyFormat,
} from "../../../../utils/constants";

type HardwareIssuesProps = {
  viewDate: string;
  customerIds: string[];
  servicePartnerIds: string[];
  customerOptions: TDropdownOptions[];
  partnerOptions: TDropdownOptions[];
  onDataFetch: (data: any) => void;
};

const HardwareIssues: React.FC<HardwareIssuesProps> = ({
  viewDate,
  customerIds,
  servicePartnerIds,
  customerOptions,
  partnerOptions,
  onDataFetch,
}) => {
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(true);
  const [tabFilter, setTabFilter] = useState<string>("Today");
  const [barSeries, setBarSeries] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any>({});

  const options = [
    { label: DateUtils.format(viewDate, dateOnlyFormat), value: "Today" },
    { label: "Last 7 days", value: "Last 7 days" },
    {
      label: DateUtils.format(viewDate, monthOnlyFormat),
      value: "In This Month",
    },
  ];

  const getIssueCatData = async () => {
    setIsLoading(true);
    try {
      const payload = {
        data: {
          viewDate: DateUtils.getDateStringWithOutTimezone(
            new Date(viewDate).toDateString()
          ),
          IsAllCustomerRequest: customerIds?.includes("ALL") ? 1 : 0,
          IsAllServicePartnerRequest: servicePartnerIds?.includes("ALL")
            ? 1
            : 0,
          customerId: customerIds?.includes("ALL")
            ? customerOptions
                ?.filter((option) => option.value !== "ALL")
                .map((option) => option.value)
            : customerIds,
          servicePartnerId: servicePartnerIds?.includes("ALL")
            ? partnerOptions
                ?.filter((option) => option.value !== "ALL")
                .map((option) => option.value)
            : servicePartnerIds,
        },
      };

      const response = await post(GetDashboardIssueCatData, payload);
      if (response) {
        const { data, dataResponse } = response;
        const { returnCode } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          const chartData = data; // Normalize keys
          onDataFetch(data);
          setChartData(chartData); // Store fetched data
          updateBarSeries(tabFilter, chartData); // Update bar series based on the current tab filter
        }
      }
    } catch (error) {
      console.error("Error fetching issue category data:", error);
    } finally {
      setIsLoading(false); // Ensure loading state is updated
    }
  };

  const updateBarSeries = (tab: string, data: any) => {
    if (data) {
      let seriesData;
      if (tab === "Today") {
        seriesData = [
          data.today.dispenserHardware,
          data.today.dispenserSoftware,
          data.today.gyroHardware,
          data.today.gyroSoftware,
        ];
      } else if (tab === "Last 7 days") {
        seriesData = [
          data.lastSevenDay.dispenserHardware,
          data.lastSevenDay.dispenserSoftware,
          data.lastSevenDay.gyroHardware,
          data.lastSevenDay.gyroSoftware,
        ];
      } else {
        seriesData = [
          data.inThisMonth.dispenserHardware,
          data.inThisMonth.dispenserSoftware,
          data.inThisMonth.gyroHardware,
          data.inThisMonth.gyroSoftware,
        ];
      }
      setBarSeries([{ data: seriesData }]); // Update bar series with the selected tab data
    }
  };

  const onChangeTabFilter = (e: RadioChangeEvent) => {
    const { value } = e.target; // Extract value from the event
    if (value) {
      setTabFilter(value); // Update state
      console.log("Tab Filter:", value);

      updateBarSeries(value, chartData); // Update bar series without calling the API
    } else {
      console.warn("No value found in the event.");
    }
  };

  useEffect(() => {
    if (customerIds?.length && servicePartnerIds?.length) {
      getIssueCatData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewDate, JSON.stringify(customerIds), JSON.stringify(servicePartnerIds)]);

  const barOptions: ApexOptions = {
    chart: {
      height: 350,
      type: "bar",
      events: {
        click: (event, chartContext, config) => {
          event.stopPropagation(); // Prevents any interaction
        },
      },
    },
    colors: ["#0EB68C"], // Example colors, replace with your color array
    plotOptions: {
      bar: {
        columnWidth: "30%",
        distributed: true, // Ensure bars are distributed
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: false,
    },
    xaxis: {
      categories: [
        ["Dispenser Hardware"],
        ["Dispenser Software"],
        ["Gyro Hardware"],
        ["Gyro Software"],
      ],
      labels: {
        style: {
          colors: ["#333333"], // Example colors, replace with your color array
          fontSize: "10px",
        },
      },
    },
    yaxis: {
      title: {
        text: "Hardware Issues",
        style: {
          color: "#333333",
          fontFamily: "Montserrat-SemiBold",
          fontSize: "10px",
        },
        offsetX: 10, // Moves the title further away from the axis
        offsetY: 10, // Adjust vertical positioning if necessary
        rotate: 270, // Ensures the text remains horizontal
      },
      labels: {
        formatter: function (value) {
          return Math.round(value).toString(); // Ensure only integers are displayed
        },
      },
      tickAmount: 5, // Adjust as per your requirements to control the number of ticks
      min: 0, // Set minimum value
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      enabled: true,
      custom: function ({ series, seriesIndex, dataPointIndex }) {
        return `<div style="padding:5px; background:#fff; color:#333; border-radius:1px;">
                ${series[seriesIndex][dataPointIndex]}
              </div>`;
      },
    },
  };

  return (
    <Card bordered={false} style={{ borderRadius: "1px", padding: "18px" }}>
      <Radio.Group
        style={{ margin: "10px", borderRadius: "1px" }}
        options={options}
        onChange={onChangeTabFilter}
        value={tabFilter}
        optionType="button"
        buttonStyle="solid"
      />
      <ReactApexChart
        loading={isLoading}
        options={barOptions}
        series={barSeries}
        type="bar" // Confirm this is 'bar'
        height={250}
      />
    </Card>
  );
};

export default HardwareIssues;
